import { put, takeEvery, takeLatest, select, take, all, call } from 'redux-saga/effects'
import { isMobileOnly } from 'react-device-detect'

import { queryClient } from '@tabeeb/services/queryClient'
import { getReadonlyPageRequest, ReadonlyWhiteboardKeys } from '@tabeeb/modules/readonlyWhiteboard/requests'
import { convertReadonlyPageToPluginPage } from '@tabeeb/services/dataConverter'
import { ContentType } from '@tabeeb/enums'
import { setPointCloudSettings } from '@tabeeb/modules/pointCloud/actions'
import { setPotreeToken } from '@tabeeb/modules/pointCloud/utils/token'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { setAutodeskViewable } from '@tabeeb/modules/contentViewer/actions'
import { getIsContentReadonly } from '@tabeeb/modules/readonlyWhiteboard/selectors'

import * as galleryActions from '../actions'
import * as accountSelectors from '../../account/selectors'
import * as gallerySelectors from '../selectors'
import * as zoomingActions from '../../playerToolbar/actions/zooming'
import * as playerActions from '../../player/actions'
import { signalrEvents, signalrActions, signalrConstants } from '../../signalr'

import * as presentationSelectors from '../../presentation/selectors'
import * as contentStateSelectors from '../../shared/content/selectors'

function* onSelectReadonlyGalleryItem({ payload: { pageId, timestamp = null } }) {
  if (!pageId) {
    return
  }

  const { selectedGalleryItemId, selectedFolderId } = yield select((state) => state.gallery.galleryState)
  let galleryItem = yield select((state) => gallerySelectors.getGalleryItemById(state, { id: pageId }))
  const isLoaded = galleryItem && galleryItem.isLoaded

  if (pageId !== selectedGalleryItemId || selectedFolderId !== galleryItem.folderId) {
    let pageTranslationModelToSet = null
    if (!isLoaded) {
      const pathname = yield select((state) => state.router.location.pathname)
      const tokenMatch = pathname.match(/^\/content\/shared\/([^/]+)/)
      const token = tokenMatch ? tokenMatch[1] : null

      yield put(galleryActions.startGalleryLoading())
      let data
      try {
        data = yield call([queryClient, queryClient.fetchQuery], {
          queryKey: [
            ReadonlyWhiteboardKeys.readonlyContent,
            {
              Token: token,
              PageId: pageId,
            },
          ],
          queryFn: getReadonlyPageRequest,
        })
      } catch (e) {
        yield put(onAddErrorNotification({ message: 'Page not found or unavailable' }))
        return
      }

      const convertedPage = convertReadonlyPageToPluginPage(data)
      if (data.ContentType === ContentType.PointCloud) {
        yield put(setPointCloudSettings(data.PointCloudPageSettings.MeasurementsScaleFactor))
        if (data.PotreeSasToken) {
          setPotreeToken({ token: data.PotreeSasToken, pageId: convertedPage.id })
        }
      }
      if (data.ContentType === ContentType.AutodeskFile) {
        if (data.TranslationModel == null) {
          yield put(onAddErrorNotification({ message: 'Failed to receive CAD settings' }))
        } else {
          pageTranslationModelToSet = data.TranslationModel
        }
      }
      yield put(galleryActions.addGalleryItem(convertedPage))
      yield put(galleryActions.setPageAsLoaded({ pageId: convertedPage.id }))
      galleryItem = convertedPage
    }

    const { foldersList } = yield select((state) => state.gallery)
    const folder = foldersList.find((item) => item.Id === galleryItem?.folderId)

    const isReturnToMainGallery = galleryItem && galleryItem.folderId === null
    if (isReturnToMainGallery || (folder && galleryItem && selectedFolderId !== galleryItem.folderId)) {
      yield put(galleryActions.clearSelection())
    }

    yield put(galleryActions.onGalleryItemSwitching())
    if (pageTranslationModelToSet != null) {
      yield put(setAutodeskViewable(pageTranslationModelToSet))
    }

    yield put(galleryActions.selectReadonlyGalleryItem(pageId))
    yield put(playerActions.resetCurrentVideoTimestamp())
    yield put(zoomingActions.onZoomValueChecked(100))

    yield put(galleryActions.onGalleryItemSwitched())

    if (timestamp !== null) {
      yield put(playerActions.seekVideoToTimestamp(timestamp * 1000))
    }
  }
}

function* onSelectGalleryItem({ payload }) {
  const { pageId, timestamp = null, updateWeb = true, force = false, suppressMessage = false } = payload
  if (!pageId) {
    return
  }

  const isContentReadonly = yield select(getIsContentReadonly)
  if (isContentReadonly) {
    yield onSelectReadonlyGalleryItem({ payload })
    return
  }
  const contentId = yield select(contentStateSelectors.getContentId)
  const presenterId = yield select(contentStateSelectors.getPresenterId)
  const currentUserId = yield select(accountSelectors.getCurrentUserId)
  let isCallStarted = yield select(presentationSelectors.getIsCallStarted)

  const { selectedGalleryItemId, selectedFolderId } = yield select((state) => state.gallery.galleryState)
  const isAvailableSelect = presenterId === currentUserId || !isCallStarted

  let galleryItem = yield select((state) => gallerySelectors.getGalleryItemById(state, { id: pageId }))
  const isLoaded = galleryItem && galleryItem.isLoaded

  if (!isAvailableSelect && !force) {
    return
  }

  if (pageId !== selectedGalleryItemId || selectedFolderId !== galleryItem.folderId) {
    if (!isLoaded) {
      yield put(galleryActions.getGalleryItemRequest({ pageId, isSelectAddedGalleryItem: force, suppressMessage }))
      yield take([galleryActions.getGalleryItemSuccess])

      galleryItem = yield select((state) => gallerySelectors.getGalleryItemById(state, { id: pageId }))

      isCallStarted = yield select(presentationSelectors.getIsCallStarted)
    }

    const { foldersList } = yield select((state) => state.gallery)
    const folder = foldersList.find((item) => item.Id === galleryItem?.folderId)

    const isReturnToMainGallery = galleryItem && galleryItem.folderId === null

    if (isReturnToMainGallery || (folder && galleryItem && selectedFolderId !== galleryItem.folderId)) {
      yield put(galleryActions.selectSessionFolder({ folderId: galleryItem.folderId, selectFirstItem: false }))
    }

    yield put(galleryActions.onGalleryItemSwitching())

    yield put(galleryActions.selectGalleryItem(pageId))
    yield put(playerActions.resetCurrentVideoTimestamp())
    yield put(zoomingActions.onZoomValueChecked(100))

    yield put(galleryActions.onGalleryItemSwitched())

    if (updateWeb) {
      const isCurrentUserPresenter = yield select(contentStateSelectors.getIsCurrentUserPresenter)

      if (isCallStarted && isCurrentUserPresenter) {
        const message = {
          pageId,
          timeStamp: Date.now(),
          type: 'setPageById',
          handleObj: {
            guid: Date.now(),
            type: 'setPageById',
            origType: 'setPageById',
          },
        }

        yield put(signalrActions.invokeHubAction({ method: 'PresenterAction', args: [contentId, message] }))
      }
    }
  }

  if (timestamp !== null) {
    yield put(playerActions.seekVideoToTimestamp(timestamp * 1000))
  }
}

function* onPresenterState(action) {
  const [{ pageId }] = action.payload

  yield put(galleryActions.onSelectGalleryItem({ pageId, force: true, updateWeb: true, suppressMessage: false }))
}

function* onPresenterAction(action) {
  if (isMobileOnly) {
    return
  }

  const [spectatorEvent] = action.payload

  const isCurrentUserPresenter = yield select(contentStateSelectors.getIsCurrentUserPresenter)
  if (isCurrentUserPresenter) {
    return
  }

  if (spectatorEvent.type === 'setPageById') {
    const { pageId } = spectatorEvent

    yield put(galleryActions.onSelectGalleryItem({ pageId, force: true, updateWeb: true, suppressMessage: false }))
  }
}

function* selectItem() {
  yield all([
    takeEvery(galleryActions.onSelectGalleryItem, onSelectGalleryItem),
    takeLatest(galleryActions.onSelectReadonlyGalleryItem, onSelectReadonlyGalleryItem),
    takeEvery(signalrEvents[signalrConstants.tabeebHubName].onPresenterState, onPresenterState),
    takeEvery(signalrEvents[signalrConstants.tabeebHubName].onPresenterAction, onPresenterAction),
  ])
}

export default selectItem
